import { useMemo } from "react";
import type { SelectValue } from "clutch/src/Select/Select";
import type { TFunction } from "i18next";
import type { DeepReadonly } from "ts-essentials";

import { readState } from "@/__main__/app-state.mjs";
import { appURLs, GAME_SHORT_NAMES } from "@/app/constants.mjs";
import { getGameProfileImg } from "@/app/util.mjs";
import type { ValorantApiMatch } from "@/data-models/valorant-api-match-data.mjs";
import type { ValorantContent } from "@/data-models/valorant-content.mjs";
import type { ValorantProfile } from "@/data-models/valorant-profile.mjs";
import {
  CONSOLE_GAME_MODES,
  GAME_MODES,
  NEW_MAP,
  STATS_GAME_MODES,
} from "@/game-val/constants.mjs";
import { GAME_SYMBOL_VAL } from "@/game-val/definition-symbol.mjs";
import type { Agents } from "@/game-val/models/agents.mjs";
import type { AbilityEffectSummary } from "@/game-val/utils/coaching.mjs";
import { sanitizeNumber, toFixedNumber } from "@/util/helpers.mjs";
import isNonNullable from "@/util/is-non-nullable.mjs";
import keyInObject from "@/util/key-in-object.mjs";
import rangeBucket from "@/util/range-bucket.mjs";
import titleCase from "@/util/title-case.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

export interface ShotStats {
  headshots: number;
  bodyshots: number;
  legshots: number;
  missedshots?: number;
}

export function getNameTag(name = "", tag = "") {
  return `${name}-${tag}`.toLowerCase();
}

export function separateNameTag(nameTag: string) {
  const [name = "", tag = ""] = nameTag.split("-");
  return { name, tag };
}

// Avg score per round
export function calcAvgScore(score: number, rounds = 1) {
  const result = score / (rounds > 0 ? rounds : 1);
  return toFixedNumber(sanitizeNumber(result), 0);
}

export function calcAvg(statsTotal: number, total: number) {
  const result = statsTotal / (total > 0 ? total : 1);
  return toFixedNumber(sanitize(result), 1);
}

export function sanitizeShotStats(
  stats: Partial<ShotStats> | null | undefined,
): ShotStats {
  return {
    headshots: stats?.headshots ?? 0,
    bodyshots: stats?.bodyshots ?? 0,
    legshots: stats?.legshots ?? 0,
  };
}

export function calcHeadshotPercent(rawStats?: Partial<ShotStats>) {
  const stats = sanitizeShotStats(rawStats);
  const total = stats.headshots + stats.bodyshots + stats.legshots;
  const sanitizedTotal = total > 0 ? total : 1;
  return toFixedNumber(
    sanitizeNumber((stats.headshots * 100) / sanitizedTotal),
    1,
  );
}

export function calcBodyshotPercent(rawStats?: Partial<ShotStats>) {
  const stats = sanitizeShotStats(rawStats);
  const total = stats.headshots + stats.bodyshots + stats.legshots;
  const sanitizedTotal = total > 0 ? total : 1;
  return toFixedNumber(
    sanitizeNumber((stats.bodyshots * 100) / sanitizedTotal),
    1,
  );
}

export function calcLegshotPercent(rawStats?: Partial<ShotStats>) {
  const stats = sanitizeShotStats(rawStats);
  const total = stats.headshots + stats.bodyshots + stats.legshots;
  const sanitizedTotal = total > 0 ? total : 1;
  return toFixedNumber(
    sanitizeNumber((stats.legshots * 100) / sanitizedTotal),
    1,
  );
}

export function calculateHitLocationPercents(damageStats: ShotStats) {
  if (!damageStats) return null;
  const headshotPercent = calcHeadshotPercent(damageStats);
  const bodyshotPercent = calcBodyshotPercent(damageStats);
  const legshotPercent = calcLegshotPercent(damageStats);

  return {
    headshotPercent,
    bodyshotPercent,
    legshotPercent,
  };
}

interface ValorantRankImageArgs {
  tier?: string | null;
  rank?: `${number}` | number;
  size?: "full" | "small";
}

export function getValorantRankImage({
  tier,
  rank,
  size = "full",
}: ValorantRankImageArgs) {
  if (tier && typeof tier === "string" && size !== "full") {
    return `${
      appURLs.CDN_PLAIN
    }/blitz/val/ranks/${tier.toLowerCase()}_small.svg?v=2.3`;
  } else if (tier && typeof tier === "string" && rank) {
    return `${
      appURLs.CDN_PLAIN
    }/blitz/val/ranks/${tier.toLowerCase()}${rank}.svg?v=2.3`;
  } else if (tier && typeof tier === "string") {
    return `${
      appURLs.CDN_PLAIN
    }/blitz/val/ranks/${tier.toLowerCase()}.svg?v=2.3`;
  }
  return `${appURLs.CDN_PLAIN}/blitz/val/ranks/unrated.svg?v=2.3`;
}

type AgentImageFormats =
  | "matchtile"
  | "header"
  | "cutout"
  | "hero"
  | "full-size-concepts";

export function getAgentImage(
  key: string,
  type: AgentImageFormats = "matchtile",
) {
  if (!key) return "";
  const agentName = getAgentKey(key);
  let agentImage = `matchtile/${agentName}-art-withBG.webp`;
  if (agentName === "kayo") agentImage = `matchtile/kayo-art-withBG-2.webp`;

  switch (type) {
    case "header": {
      agentImage = `${agentName}/${agentName}-concept-headshot.webp`;
      if (agentName === "kayo") agentImage = `matchtile/kayo-art-withBG-2.webp`;
      break;
    }
    case "cutout": {
      agentImage = `${key}/${key}-cutout-compressed.webp`;
      if (key === "jett") agentImage = `jett/jett-cutout3-compressed.webp`;
      break;
    }
    case "hero": {
      agentImage = `heroes/${agentName}.webp`;
      break;
    }
    case "full-size-concepts": {
      agentImage = `full-size-concepts/${agentName}.webp`;
      break;
    }
    case "matchtile":
    default:
      break;
  }

  return appURLs.CDN + `/blitz/val/agents/${agentImage}`;
}

export const getActData = (content: ValorantContent) =>
  content?.acts?.reduce((actData, act) => {
    actData[act?.uuid] = act;
    return actData;
  }, {});

interface ActOptions {
  text: Translation;
  value: string;
  key: string;
  short: string;
  episode: string;
  act: string;
  endedAt?: Date | null;
}

type EndedValorantActs = Omit<ValorantContent["acts"][number], "endedAt"> & {
  endedAt: NonNullable<ActOptions["endedAt"]>;
};

export const getActOptions = (
  acts: DeepReadonly<ValorantContent["acts"]> = [],
  type: "id" | "short" = "id",
): ActOptions[] => {
  const latestActIndex = acts.findIndex((a) => a.endedAt === null);
  const latestAct = acts[latestActIndex];
  const filteredActs: ValorantContent["acts"] = acts
    .reduce<EndedValorantActs[]>((acc, act) => {
      if (!isNonNullable(act.endedAt)) return acc;
      acc.push({
        ...act,
        endedAt:
          act.endedAt instanceof Date ? act.endedAt : new Date(act.endedAt),
      });
      return acc;
    }, [])
    .sort((a, b) => {
      const aEndedAt = a.endedAt.getTime();
      const bEndedAt = b.endedAt.getTime();
      return aEndedAt - bEndedAt;
    });

  if (latestAct) filteredActs.push(latestAct);

  return filteredActs
    .map(({ endedAt, uuid, name, episode: { name: epName } }): ActOptions => {
      const episodeName = epName.replace("EPISODE ", "Ep ");
      const actName = name.replace("ACT ", "Act ");
      const short =
        `e${epName.replace("EPISODE ", "")}act${name.replace("ACT ", "")}`.toLowerCase(); // potentially subject to change
      return {
        text: [
          `val:actName`,
          `{{ episode, string }} - {{ act, string }}`,
          { episode: episodeName, act: actName },
        ] as const,
        value: type === "id" ? uuid : short,
        key: uuid,
        short,
        episode: epName,
        act: name,
        endedAt,
      };
    })
    ?.reverse()
    ?.slice(0, 3);
};

export const getActId = (
  actName: string,
  actData: ValorantContent["acts"] | DeepReadonly<ValorantContent["acts"]>,
) => {
  const actOptions = getActOptions(actData, "short");
  return actOptions.find((option) => option.value === actName)?.key;
};

export const getActiveSeason = (
  allActs: ValorantContent["acts"] | DeepReadonly<ValorantContent["acts"]>,
) => {
  if (!allActs) return null;
  const actOptions = getActOptions(allActs);
  const currentActId = (allActs ?? []).find(
    (act) => act.endedAt === null && act.episode.endedAt === null,
  )?.uuid;
  const actAndEp = actOptions.find((o) => o.value === currentActId);
  return { id: currentActId, name: actAndEp?.short };
};

export const getPreviousAct = (
  allActs: DeepReadonly<ValorantContent["acts"]>,
) => {
  if (!allActs) return null;
  const actOptions = getActOptions(allActs);
  return actOptions[1];
};

export function translateValRankedTier(t: TFunction, tier: string) {
  const str = tier.toLowerCase();
  return t(`val:ranks:${str}`, "{{rank}}", { rank: titleCase(str) });
}

export const getMapDisplayName = (map: string) => {
  //port -> icebox //todo:this mapping should be pulled down from json with ranks soon to be added
  if (map.toLowerCase() === "port") return "Icebox";
  return map;
};

const kdaBucket = rangeBucket([
  [0, "#828790"],
  [1, "#978D87"],
  [1.25, "#C4A889"],
  [1.5, "#DEAF78"],
  [1.75, "#E6A85F"],
  [2, "#FF9417"],
]);

export function kdaColor(kda: number) {
  return kdaBucket[kda];
}

const scoreColorBucket = rangeBucket([
  [0, "#E44C4D"],
  [160, "#DD7A7D"],
  [180, "#D5B1B5"],
  [200, "#C1D4D8"],
  [220, "#9DD5D7"],
  [240, "#79D6D6"],
  [260, "#30D9D4"],
]);

// Color range for combat score
export function scoreColorStyle(score: number) {
  return scoreColorBucket[score];
}

export function getDeathmatchPositionColor(position: number) {
  const colors = {
    1: "var(--yellow)",
    2: "#A6ACB9",
    3: "#AE8967",
    4: "var(--shade2)",
  };
  if (keyInObject(colors, position)) return colors[position];
  return "var(--shade3)";
}

export function getOutcomeColor(
  queue: string,
  winStatus: string,
  matchPosition: number,
) {
  if (isQueueDeathmatch(queue)) {
    return getDeathmatchPositionColor(matchPosition);
  }
  // isComp
  if (winStatus === "loss") return "var(--red)";
  if (winStatus === "win") return "var(--turq)";
  return "var(--shade3)";
}

export function getOutcomeShort(t: TFunction, outcome: string) {
  switch (outcome) {
    case "win":
      return t("val:outcomes.winShort", "W");
    case "loss":
      return t("val:outcomes.lossShort", "L");
    case "tie":
      return t("val:outcomes.drawShort", "D");
    default:
      return "-";
  }
}

export function getOutcomeShortColor(outcome: string) {
  if (outcome === "loss") return "var(--red)";
  if (outcome === "win") return "var(--turq)";
  return "var(--shade1)";
}

const weaponDamageBucket = rangeBucket([
  [0, "#E44C4D"],
  [15, "#DD7A7D"],
  [45, "#D5B1B5"],
  [75, "#C1D4D8"],
  [90, "#9DD5D7"],
  [120, "#79D6D6"],
]);
// // Color range for wep avg dmg
export function weaponDamageColor(score: number) {
  return weaponDamageBucket[score];
}

const winRateBucket = rangeBucket([
  [0, "var(--perf-neg3)"],
  [0.46, "var(--perf-neg2)"],
  [0.48, "var(--perf-neg1)"],
  [0.5, "var(--perf-neutral)"],
  [0.51, "var(--perf-pos1)"],
  [0.53, "var(--perf-pos2)"],
  [0.55, "var(--perf-pos3)"],
]);
export const getWinRateColor = (rate: number) => winRateBucket[rate];

export const rankColor = {
  iron: "#757E80",
  bronze: "#A78260",
  silver: "#BAC7C8",
  gold: "#CDA353",
  platinum: "#4CACB9",
  diamond: "#A462EC",
  ascendant: "#6AE2AF",
  immortal: "#CB2C56",
  radiant: "#D6BE64",
  unrated: "var(--shade3)",
};

export const getQueueName = (queue: string | null) => {
  if (typeof queue !== "string") return null;
  if (queue === "") return "Custom";
  if (queue.toLowerCase() === "ggteam") return "Escalation";
  if (queue.toLowerCase() === "onefa") return "Replication";
  if (queue.toLowerCase() === "hurm") return "Team Deathmatch";
  if (queue.toLowerCase() === "console_unrated") return "Console Unrated";
  if (queue.toLowerCase() === "console_swiftplay") return "Console Swiftplay";
  if (queue.toLowerCase() === "console_hurm") return "Console Team Deathmatch";
  if (queue.toLowerCase() === "console_deathmatch") return "Console Deathmatch";
  if (queue.toLowerCase() === "newmap") return NEW_MAP;
  return titleCase(queue);
};

export const getQueueVal = (displayValue: string) => {
  if (displayValue === "escalation") return "ggteam";
  if (displayValue === "replication") return "onefa";
  if (displayValue === "Team Deathmatch") return "hurm";
  if (displayValue === "Console Unrated") return "console_unrated";
  if (displayValue === "Console Swiftplay") return "console_swiftplay";
  if (displayValue === "Console Team Deathmatch") return "console_hurm";
  if (displayValue === "Console Deathmatch") return "console_deathmatch";
  if (displayValue === NEW_MAP) return "newmap";
  return displayValue;
};

export const getGameModes = (
  stats: boolean = false,
  isConsole: boolean = false,
): SelectValue<string>[] => {
  if (isConsole)
    return CONSOLE_GAME_MODES.map((modeName) => {
      const value = getQueueVal(modeName);
      return {
        disabled: false,
        text: [
          `val:queues.${modeName}`,
          titleCase(modeName.replace("console_", "")),
        ] as Translation,
        value,
      };
    });
  return (stats ? STATS_GAME_MODES : GAME_MODES).map((modeName) => {
    const value = getQueueVal(modeName);
    return {
      disabled: false,
      text: [`val:queues.${modeName}`, titleCase(modeName)] as Translation,
      value,
    };
  });
};

export const isQueueCompetitive = (queue: string) => {
  return queue === "competitive" || queue === "console_competitive";
};

export const isQueueDeathmatch = (queue: string) => {
  return queue === "console_deathmatch" || queue === "deathmatch";
};

export const isQueueTeamDeathmatch = (queue: string) => {
  return queue === "console_hurm" || queue === "hurm";
};

export const isQueueEscalation = (queue: string) => {
  return queue === "ggteam" || queue === "console_ggteam";
};

interface GraphMargin {
  marginTop: number;
  marginBottom: number;
}

export const getGraphPoint = (
  val: number,
  xIndex: number,
  xInterval: number,
  minVal: number,
  maxVal: number,
  graphHeight: number,
  graphWidth: number,
  margin: GraphMargin,
  isReversedX: boolean | null | undefined,
  xShiftIndex: number,
) => {
  const { marginTop, marginBottom } = margin;
  const minGraphVal = marginTop;
  const maxGraphVal = graphHeight - marginBottom;
  const scale = (minGraphVal - maxGraphVal) / (maxVal - minVal || 1);

  const posY = (val - minVal) * scale + maxGraphVal;
  let posX = xInterval / 2 + xInterval * (xIndex + xShiftIndex);
  posX = isReversedX ? graphWidth - posX : posX;

  return { posX, posY };
};

interface GetGraphPointsParams {
  diffEnd: number;
  diffStart?: number | null;
  graphHeight: number;
  graphValues: number[];
  graphWidth: number;
  isLeftAligned?: boolean;
  isReversedX?: boolean | null;
  isStretch?: boolean;
  limits: {
    maxValue: number;
    minValue: number;
    avgValue: number;
  };
  margin: GraphMargin;
  pointCount: number;
}

export const getGraphPoints = (params: GetGraphPointsParams) => {
  const {
    graphHeight,
    graphWidth,
    pointCount,
    graphValues,
    isStretch,
    isReversedX,
    margin,
    limits,
    isLeftAligned = true,
    diffStart,
    diffEnd,
  } = params;

  const { maxValue, minValue, avgValue } = limits;

  // Get number of graph to display.
  const numPoints = pointCount
    ? Math.min(pointCount, graphValues.length)
    : graphValues.length;
  const numBreakBars = isStretch ? graphValues.length : pointCount;

  // Calculate width of break bar in x direction, [px]
  const colWidth = graphWidth / numBreakBars;

  // Get values of graph to display
  const data = graphValues?.slice(0, numPoints);

  // Calculate the start value of graph.
  let startVal = 0;
  if (diffStart === undefined || diffStart === null) {
    if (graphValues?.length) {
      const x1 = graphValues[0];
      const x2 = graphValues.length > 1 ? graphValues[1] : graphValues[0];
      startVal = x1 - (x2 - x1) / 2;
    }
  } else {
    startVal = (graphValues?.[0] ?? 0) + diffStart / 2;
  }

  let endVal = 0;
  if (diffEnd === undefined || diffEnd === null) {
    if (graphValues?.length) {
      const graphLength = graphValues.length;
      const x2 = graphValues[graphLength - 1];
      const x1 =
        graphValues.length > 1
          ? graphValues[graphLength - 2]
          : graphValues[graphLength - 1];
      endVal = x2 + (x2 - x1) / 2;
    }
  } else {
    endVal = (graphValues?.[graphValues.length - 1] ?? 0) + diffEnd / 2;
  }

  // const upperValueGap = Math.max(maxValue, startVal, endVal) - avgValue;
  // const lowerValueGap = avgValue - Math.min(minValue, startVal, endVal);
  // const maxValueGap = Math.max(upperValueGap, lowerValueGap);
  const fixedMaxValue = maxValue; // Math.ceil(maxValueGap + avgValue);
  const fixedMinValue = minValue; // Math.floor(avgValue - maxValueGap);

  const isShifted =
    !isStretch &&
    ((isReversedX && isLeftAligned) || !(isReversedX || isLeftAligned));
  const xShiftIndex = isShifted
    ? numBreakBars - Math.min(graphValues.length, numBreakBars)
    : 0;

  // Get Graph Points
  const graphPoints = data?.map((val, index) => {
    return getGraphPoint(
      val,
      index,
      colWidth,
      fixedMinValue,
      fixedMaxValue,
      graphHeight,
      graphWidth,
      margin,
      isReversedX,
      xShiftIndex,
    );
  });
  // Get Point for average value
  const avgPoint = getGraphPoint(
    avgValue,
    0,
    colWidth,
    fixedMinValue,
    fixedMaxValue,
    graphHeight,
    graphWidth,
    margin,
    isReversedX,
    xShiftIndex,
  );

  // Get start point
  const startPoint = getGraphPoint(
    startVal,
    0,
    colWidth,
    fixedMinValue,
    fixedMaxValue,
    graphHeight,
    graphWidth,
    margin,
    isReversedX,
    xShiftIndex,
  );
  startPoint.posX += isReversedX ? colWidth / 2 : -colWidth / 2;
  startPoint.posY += 0.1;

  // Get end point
  const endPoint = getGraphPoint(
    endVal,
    graphValues.length - 1,
    colWidth,
    fixedMinValue,
    fixedMaxValue,
    graphHeight,
    graphWidth,
    margin,
    isReversedX,
    xShiftIndex,
  );
  endPoint.posX += isReversedX ? -colWidth / 2 : colWidth / 2;

  return {
    graphPoints,
    startPoint,
    endPoint,
    avgPoint,
  };
};

export const meanBy = (values: number[]) => {
  let avg = 0;
  for (let i = 0; i < values.length; i++) {
    avg += values[i];
  }
  return avg / values.length;
};

export const getDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.getMonth() + 1 + "/" + date.getDate();
};

export const getAvgHeadshot = (rawStats?: Partial<ShotStats>) => {
  if (rawStats) {
    const stats = sanitizeShotStats(rawStats);
    return (
      (stats.headshots * 100) /
      (stats.headshots +
        stats.bodyshots +
        stats.legshots +
        (stats?.missedshots ?? 0) || 1)
    ).toFixed(1);
  }
  return null;
};

export const getSprayControl = (rawStats?: Partial<ShotStats>) => {
  if (!rawStats) return 0;
  const stats = sanitizeShotStats(rawStats);
  return (
    ((stats?.missedshots ?? 0) * 100) /
    (stats.headshots +
      stats.bodyshots +
      stats.legshots +
      (stats?.missedshots ?? 0) || 1)
  ).toFixed(1);
};

export const safeDivide = (a?: number | null, b?: number | null) => {
  return (a || 0) / (b || 1);
};

export const getMatchDataWithPerfStats = (
  matchPerformanceStats,
  profileId: string,
  agents: Agents,
) => {
  if (!matchPerformanceStats) return [];
  return matchPerformanceStats
    .map((performanceMatch) => {
      const agentKey = performanceMatch.agentId;
      const agent = agents.find((a) => a.key === agentKey);
      return {
        date: getDate(performanceMatch.matchDate),
        hs: getAvgHeadshot(performanceMatch?.current) || 0,
        sprayControl: getSprayControl(performanceMatch?.current) || 0,
        crosshair:
          100 -
            safeDivide(
              performanceMatch?.current?.distance,
              performanceMatch?.current?.crosshairplacementCount,
            ) || 0,
        link: `/valorant/match/${profileId}/${performanceMatch.season}/${performanceMatch.matchId}`,
        tooltipData: {
          hs: {
            current: getAvgHeadshot(performanceMatch?.current) || 0,
            avg: getAvgHeadshot(performanceMatch?.lastN) || 0,
          },
          sprayControl: {
            current: getSprayControl(performanceMatch?.current) || 0,
            avg: getSprayControl(performanceMatch?.lastN) || 0,
          },
          crosshair: {
            current:
              100 -
                safeDivide(
                  performanceMatch?.current?.distance,
                  performanceMatch?.current?.crosshairplacementCount,
                ) || 0,
            avg:
              100 -
                safeDivide(
                  performanceMatch?.lastN?.distance,
                  performanceMatch?.lastN?.crosshairplacementCount,
                ) || 0,
          },
          playerIcon: agent?.images.matchtile.url,
        },
      };
    })
    .slice(0, 10)
    .reverse();
};

export function sanitize(number: number) {
  return Number.isNaN(number) ? 0 : number;
}

// Winrate
export function calcWinrate(stats: Record<string, number> = {}) {
  let result = 0;

  if (stats.ties) {
    result = (stats.wins * 100) / (stats.matches - stats.ties || 1);
  } else {
    result = (stats.wins * 100) / (stats.matches > 0 ? stats.matches : 1);
  }

  return sanitize(result);
}

// Pickrate
export function calcPickRate(matches: number, totalMatches: number) {
  const result = (matches * 100) / (totalMatches > 0 ? totalMatches : 1);
  return sanitize(result);
}

export function formatProfileForNav(profile) {
  return {
    id: profile.internalUuid,
    name: profile.gameName,
    imgUrl: getGameProfileImg(GAME_SYMBOL_VAL, profile),
    url:
      profile.gameName && profile.tagLine
        ? `/valorant/profile/${getNameTag(profile.gameName, profile.tagLine)}`
        : "",
    profileUrl:
      profile.gameName && profile.tagLine
        ? `/valorant/profile/${getNameTag(profile.gameName, profile.tagLine)}`
        : "",
    tagLine: profile.tagLine,
  };
}

export function nameTagNullCheck(nameTag) {
  return nameTag !== "null-null";
}

export type FormattedProfile = {
  internalUuid: string;
  gameName: string;
  tagLine: string;
  lastPlayedAt: Date;
  level: number;
  xp: number;
  region: string;
  userAccountId: string | null;
  playerCardId: string;
};

export const formatProfile = (player): FormattedProfile => ({
  internalUuid: player.id,
  gameName: player.name,
  tagLine: player.tag,
  lastPlayedAt: new Date(player.lastPlayed),
  level: player.level,
  xp: player.xp,
  region: player.region,
  userAccountId: readState.user?.id,
  playerCardId: player.playerCardId,
});

export const formatConnectedProfile = (profile) => ({
  internalUuid: profile.valorantProfile.internalUuid,
  gameName: profile.gameName,
  tagLine: profile.tagLine,
  region: profile.valorantProfile.region,
  level: profile.valorantProfile.level,
  xp: profile.valorantProfile.xp,
  lastPlayed: profile.valorantProfile.lastPlayedAt,
  playerCardId: profile.valorantProfile.playerCardId,
  lastAgentName: profile.valorantProfile.lastAgent?.name,
  userAccountId: profile.valorantProfile.userAccountId,
  isPrivate: profile.valorantProfile.private,
});

export const getRRChange = (rr) => {
  let ratingChange = rr.ratingAfter - rr.ratingBefore;
  if (rr.tierAfter > rr.tierBefore) {
    ratingChange = 100 - rr.ratingBefore + rr.ratingAfter;
  } else if (rr.tierAfter < rr.tierBefore) {
    ratingChange = (100 - rr.ratingBefore + (100 - rr.ratingAfter)) * -1;
  }

  return ratingChange;
};
export const getAgentName = (
  t: TFunction,
  agentKey: string,
  agentName: string,
) => {
  if (agentKey) {
    return t(`val:agents.${agentKey}`, agentName);
  }

  return t("val:agents.unknownAgent", "Unknown Agent");
};

export const useMapsList = () => {
  const {
    val: {
      cms: { maps: list },
    },
  } = useSnapshot(readState);

  const mapsList = useMemo(
    () =>
      list
        .map((l) => ({
          href: `/${GAME_SHORT_NAMES[GAME_SYMBOL_VAL]}/lineups/${l.key}`,
          text: l.name,
          ...l,
        }))
        .sort((l, r) => (r.key < l.key ? 1 : -1)),
    [list],
  );

  return mapsList;
};

type PartialPlayer = Partial<{
  valorantProfile: {
    internalUuid: string;
  };
  puuid: string;
}>;

export type PlayerMatch = ValorantApiMatch["playerMatches"][number];

export interface HitLocationPercents {
  headshotPercent: number;
  bodyshotPercent: number;
  legshotPercent: number;
}

export function getAbilityKills(stats: PlayerMatch) {
  return {
    ability1Casts: {
      kills: stats.abilityOneKills,
      casts: stats.abilityOneCasts,
    },
    ability2Casts: {
      kills: stats.abilityTwoKills,
      casts: stats.abilityTwoCasts,
    },
    grenadeCasts: {
      kills: stats.grenadeKills,
      casts: stats.grenadeCasts,
    },
    ultimateCasts: {
      kills: stats.ultimateKills,
      casts: stats.ultimateCasts,
    },
  };
}

export function getPlayerId(player: PartialPlayer) {
  return player?.valorantProfile?.internalUuid || player?.puuid;
}

/**
 * Returns a single player match for a given riot profile.
 *
 * @param matchInfo
 * @param profile
 */
export function getPlayerMatchStats(
  matchInfo: DeepReadonly<ValorantApiMatch>,
  profile: ValorantProfile,
): PlayerMatch | null {
  return matchInfo && profile
    ? matchInfo?.playerMatches?.find(
        (p) =>
          p.valorantProfile.riotAccount.puuid === profile?.puuid ||
          p.valorantProfile.riotAccount.puuid ===
            profile?.valorantProfile?.internalUuid,
      )
    : null;
}

export type AbilitySlot =
  | "ability1Casts"
  | "ability2Casts"
  | "grenadeCasts"
  | "ultimateCasts";
export type AbilitySlotNames =
  | "ability1"
  | "ability2"
  | "abilityOne"
  | "abilityTwo"
  | "grenade"
  | "ultimate"
  | keyof AbilityEffectSummary;

/**
 * Normalize ability keys to match the AGENT_ABILITY_CASTS dict.
 *
 * @see {@link AGENT_ABILITY_CASTS}
 */
export function mapAbilitySlotNameToCasts(
  name: AbilitySlotNames | AbilitySlot,
): AbilitySlot {
  const mapToCasts: Record<AbilitySlotNames, AbilitySlot> = {
    ability1: "ability1Casts",
    ability2: "ability2Casts",
    abilityOne: "ability1Casts",
    abilityTwo: "ability2Casts",
    abilityOneEffects: "ability1Casts",
    abilityTwoEffects: "ability2Casts",
    grenade: "grenadeCasts",
    grenadeEffects: "grenadeCasts",
    ultimate: "ultimateCasts",
    ultimateEffects: "ultimateCasts",
  };

  return mapToCasts?.[name] ?? name;
}

/**
 * Takes an ability slot name, and returns the ability key for the specified agent.
 *
 * @param abilityId A key referencing the ability. Will be mapped to the appropriate key on AGENT_ABILITY_CASTS
 * @param abilities The abilities to search in.
 * @returns The found ability id
 */
export function getAbilityKeyFromMeta(
  abilityId: AbilitySlot | AbilitySlotNames,
  abilities: DeepReadonly<Agents[number]["abilities"]>,
): Agents[number]["abilities"][number] {
  const cast = mapAbilitySlotNameToCasts(abilityId);
  return abilities.find((ability) => {
    if (ability.keybind === "Q" && cast === "ability1Casts") return true;
    if (ability.keybind === "E" && cast === "ability2Casts") return true;
    if (ability.keybind === "C" && cast === "grenadeCasts") return true;
    if (ability.keybind === "X" && cast === "ultimateCasts") return true;
    return false;
  });
}

export const getExpiryTime = () => Date.now() + 5 * 60 * 1000;

type HSLValue = `${number}, ${number}%, ${number}%`;
export const getHSLColor = (color: Agents[number]["color"]): HSLValue =>
  `${color.h}, ${color.s}%, ${color.l}%`;

export const getAgentKey = (name?: string): string =>
  name?.toLowerCase().replace("/", "");
